import { CheckCircle2, KeyRound, TriangleAlert, Trash2, Wand2 } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { useDeleteCredential, useSaveCredential, useTestCredential } from '@/api/hooks'
import type { ProviderInfo } from '@/api/types'
import { Button, Input, useToast } from '@/components/ui'
import { fieldErrorOf, generalErrorOf } from '@/features/auth/authErrors'
import { formatDate } from '@/lib/format'

import { Callout } from './Callout'
import { deezerBookmarklet } from './deezerBookmarklet'
import { SettingsDisclosure } from './SettingsDisclosure'

/** Where to find the value, per provider. Vague instructions are useless here. */
const INSTRUCTIONS: Partial<Record<string, { label: string; help: React.ReactNode }>> = {
  deezer: {
    label: 'Your Deezer cookie (arl)',
    help: (
      <>
        Sign in at <span className="font-medium text-text">deezer.com</span>, then open your
        browser&rsquo;s developer tools and go to{' '}
        <span className="font-medium text-text">Application → Cookies → https://www.deezer.com</span>.
        Copy the whole value of the cookie named <code className="text-text">arl</code>.
      </>
    ),
  },
}

export function CredentialForm({ provider }: { provider: ProviderInfo }) {
  const inputId = useId()
  const toast = useToast()

  const [secret, setSecret] = useState('')
  const save = useSaveCredential()
  const test = useTestCredential()
  const remove = useDeleteCredential()

  const credential = provider.credential
  const copy = INSTRUCTIONS[provider.slug]
  const busy = save.isPending || test.isPending || remove.isPending

  /**
   * Accept a cookie handed over by the bookmarklet in the URL fragment.
   *
   * The fragment is stripped from history *before* anything else happens — no
   * await, no render, no logging in between — because until that runs, a live
   * credential is sitting in the address bar and in that tab's history entry.
   * Fragments are never sent to a server, so it never reaches nginx or Django on
   * the way in; this is about the browser's own copy.
   */
  const handedOver = useRef(false)
  useEffect(() => {
    if (provider.slug !== 'deezer' || handedOver.current) return
    const match = /(?:^|[#&])arl=([a-f0-9]{192})\b/i.exec(window.location.hash)
    if (!match) return

    handedOver.current = true
    const value = match[1]
    window.history.replaceState(null, '', window.location.pathname + window.location.search)

    save.mutate(
      { provider: 'deezer', secret: value },
      {
        onSuccess: (result) =>
          result.status === 'valid'
            ? toast.success('Deezer connected', 'Picked up from your browser.')
            : toast.error('Deezer did not accept that cookie', result.message),
        onError: () => toast.error('Could not save that cookie', 'Try pasting it by hand.'),
      },
    )
    // Runs once per arrival; `save` and `toast` are stable for the mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.slug])

  const onSave = (event: React.FormEvent) => {
    event.preventDefault()
    if (!secret.trim()) return
    save.mutate(
      { provider: provider.slug, secret: secret.trim() },
      {
        onSuccess: (result) => {
          setSecret('')
          if (result.status === 'valid') {
            toast.success(`${provider.name} connected`, result.message)
          } else {
            toast.error(`${provider.name} did not accept that`, result.message)
          }
        },
      },
    )
  }

  return (
    <form onSubmit={onSave} className="mt-3 border-t border-border pt-3">
      {credential?.has_credential ? (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="flex items-center gap-1.5 text-sm text-text">
            {credential.status === 'valid' ? (
              <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
            ) : (
              <TriangleAlert className="h-4 w-4 text-warning" aria-hidden="true" />
            )}
            Saved
            <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs text-text-muted">
              {credential.preview}
            </code>
          </span>
          {credential.checked_at ? (
            <span className="text-xs text-text-subtle">
              Checked {formatDate(credential.checked_at).toLowerCase()}
            </span>
          ) : null}

          <span className="ml-auto flex gap-1.5">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={test.isPending}
              disabled={busy}
              onClick={() =>
                test.mutate(provider.slug, {
                  onSuccess: (result) =>
                    result.status === 'valid'
                      ? toast.success(`${provider.name} is working`, result.message)
                      : toast.error(`${provider.name} is not working`, result.message),
                })
              }
            >
              Test
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={remove.isPending}
              disabled={busy}
              onClick={() =>
                remove.mutate(provider.slug, {
                  onSuccess: () => toast.info(`Removed your ${provider.name} cookie`),
                })
              }
            >
              <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
              Remove
            </Button>
          </span>
        </div>
      ) : null}

      {credential?.has_credential && !credential.readable ? (
        <Callout tone="danger" icon={KeyRound} className="mb-3">
          This saved value can no longer be decrypted, which happens when the server&rsquo;s secret
          key changes. Paste it again to fix it.
        </Callout>
      ) : null}

      {credential?.has_credential && credential.status === 'invalid' && credential.message ? (
        <Callout tone="warning" icon={TriangleAlert} className="mb-3">
          {credential.message}
        </Callout>
      ) : null}

      <Input
        id={inputId}
        // A cookie is a password in every way that matters, so never render it in
        // plain text. "new-password" rather than "off" because Chrome ignores
        // "off" on password inputs and will still offer to fill or save it.
        type="password"
        autoComplete="new-password"
        spellCheck={false}
        label={provider.credential_label || copy?.label || `${provider.name} credential`}
        placeholder={credential?.has_credential ? 'Paste a new value to replace it' : 'Paste the value here'}
        value={secret}
        onChange={(event) => setSecret(event.target.value)}
        error={fieldErrorOf(save.error, 'secret')}
        disabled={busy}
      />

      <div className="mt-3">
        <SettingsDisclosure title="Connection help">
          {provider.slug === 'deezer' ? (
            <div className="mt-2 rounded-card border border-border bg-surface-sunken p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-text">
                <Wand2 className="h-4 w-4 text-accent" aria-hidden="true" />
                Skip the devtools
              </p>
              <p className="mt-1 text-sm text-text-muted">
                Drag this to your bookmarks bar, then click it while signed in to{' '}
                <span className="font-medium text-text">deezer.com</span>. It reads the cookie and
                brings you back here with it saved.
              </p>
              {/* A real anchor so it can be dragged to the bookmarks bar — the only
                  way to install a bookmarklet without retyping the `javascript:`
                  prefix, which browsers strip from a URL pasted into a bookmark.

                  The href is set through a ref rather than as a prop because React
                  refuses to render a `javascript:` href and substitutes a throwing
                  stub, which would ship a bookmark that errors when clicked. That
                  guard is aimed at `javascript:` URLs built from user input; this one
                  is a constant plus `location.origin`, so writing it to the DOM
                  directly is the intended escape hatch, not a way around a real
                  protection.

                  It must not navigate if clicked here — on this origin it does
                  nothing useful, so clicking explains how to install it instead. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                ref={(node) => {
                  if (node) node.setAttribute('href', deezerBookmarklet(window.location.origin))
                }}
                onClick={(event) => {
                  event.preventDefault()
                  toast.info(
                    'Drag it, don\u2019t click it',
                    'Drag this button to your bookmarks bar, then click it from a Deezer tab.',
                  )
                }}
                draggable
                className="mt-2 inline-flex h-9 cursor-grab items-center gap-1.5 rounded-full border border-accent bg-surface-raised px-3 text-sm font-medium text-accent active:cursor-grabbing"
              >
                Get my Deezer cookie
              </a>
              <p className="mt-2 text-xs text-text-subtle">
                It asks Deezer for the cookie in your own browser. Your password is never involved,
                and nothing is sent anywhere except back to Tunes.
              </p>
            </div>
          ) : null}

          {provider.credential_help || copy?.help ? <p className="mt-1.5 text-sm text-text-muted">{provider.credential_help || copy?.help}</p> : null}
        </SettingsDisclosure>
      </div>

      {generalErrorOf(save.error, ['secret']) ? (
        <Callout tone="danger" className="mt-3">
          {generalErrorOf(save.error, ['secret'])}
        </Callout>
      ) : null}

      <div className="mt-3">
        <Button type="submit" size="sm" loading={save.isPending} disabled={busy || !secret.trim()}>
          {credential?.has_credential ? 'Replace and test' : 'Save and test'}
        </Button>
        {secret.trim() ? <span className="ml-3 text-sm text-text-muted">Unsaved changes</span> : null}
      </div>
    </form>
  )
}
